import React, { useEffect, useState, useContext } from "react";
import { compose, withApollo } from "react-apollo";
import { connect } from "react-redux";
import ConfirmDialog from "../../../components/shared/ConfirmDialog";
import { selectAllowedRolesFromApps } from "../../../selectors/applications";
import useDialog from "../../../utils/hooks/useDialog";
import { loadUserInfo as loadUserInfoActions } from "../../landingPageUser/redux/actions";
import TopBannerMessagePageModal from "./TopBannerMessagePageModal";
import { metaData } from "./metaData";
import { SettingsContext } from "../../../contexts";
import BUTTON_MODEL_LABEL from "../../../utils/constants/buttonModelLabel";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { generateID } from "@digitallab/grid-common-components";
import { Module, NOTIFICATIONS_BANNER_ACTIVE_STATUS, NOTIFICATIONS_BANNER_SITE_FILTER } from "../../../constants";
import TopBannerMessageList from "./TopBannerMessageList";
import { OwcButton, OwcListItem, OwcSelectDropdown, OwcTypography } from "@one/react";
import "./TopBannerMessagePageTable.scss";
import { deleteApplicationFactory } from "./deleteApplicationFactory";
import CustomSingleSelect from "../../../components/shared/CustomSingleSelect";
import { emptyApplication } from "./emptyApplication";

const { applicationTable } = Module.PAGE.ADMIN;

const siteOptions = Object.values(NOTIFICATIONS_BANNER_SITE_FILTER).map((value) => ({
  value,
  label: value
}));

const activeOptions = Object.values(NOTIFICATIONS_BANNER_ACTIVE_STATUS).map((value) => ({
  value,
  label: value
}));

const TopBannerMessagePageTable = ({ data, refetch, categories, allowedRoles, client, loadUserInfo, editApplication }) => {
  const { trackEvent } = useMatomo();

  const [pageNo, setPageNo] = useState(1);
  const [showPerPage, setShowPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const { openDialog, ...dialogProps } = useDialog();
  const [filteredData, setFilteredData] = useState(data);
  const [siteName, setSiteName] = useState("Show All");
  const [isActive, setIsActive] = useState("Yes");
  const [orderBy, setOrderBy] = useState(["from"]);
  const [isReverseOrder, setIsReverseOrder] = useState(false);
  const { application, setApplication } = useContext(SettingsContext);

  const filterData = (q, a) => {
    const allData = filterByQueryForTopBarMessage(data, q, a);
    setTotalCount(allData.length);
    const lowIndex = pageNo === 1 ? 0 : (pageNo - 1) * showPerPage;
    const highIndex = pageNo === 1 ? showPerPage : lowIndex + showPerPage;
    const paginationData = allData.slice(lowIndex, highIndex);
    setFilteredData(paginationData);
  };


  const filterByQueryForTopBarMessage = (data, site, active) => {

    if (site === "Show All" && active === "All Status") {
      return data;
    }
    const now = new Date();
    const targetDate = new Date(Date.UTC(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ));

    return data.filter(item => {
      const matchSiteName = site === "Show All" || item.site === site;
      const start = new Date(item.from);
      const end = new Date(item.to);
      const isBetweenRange = targetDate >= start && targetDate <= end;
      const resultsActive =
        active === "All Status" ? true :
          active === "Yes" ? isBetweenRange : !isBetweenRange;
      return matchSiteName && resultsActive;
    });
  };

  useEffect(() => {

    filterData(siteName, isActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isReverseOrder, pageNo, showPerPage, siteName, isActive]);


  const deleteApplication = deleteApplicationFactory({
    loadUserInfoFn: loadUserInfo,
    client,
    data,
    setApplicationToDelete,
    refetch
  });

  const handlerOnSetApp = (app) => {
    // Track click on button
    trackEvent({ category: "Add Banner", action: "click-event" });
    setApplication(app);
  };

  return (
    <div className="top-banner-message-page-table">
      <ConfirmDialog
        {...dialogProps}
        approveText="Delete banner"
        approveColor="error"
        approveVariant="contained"
        cancelText="Cancel"
        cancelVariant="outlined"
        cancelColor="primary"
        onApprove={() => deleteApplication(applicationToDelete)}
        onCancel={() => setApplicationToDelete(null)}
        title="Delete banner"
        content={`Are you sure you want to remove this banner?`}
      />
      <TopBannerMessagePageModal
        id={generateID.UUID(applicationTable, `applicationtable`, "table")}
        applications={data}
        open={application !== null}
        title={application?.id ? BUTTON_MODEL_LABEL.editBanner : BUTTON_MODEL_LABEL.addBanner}
        allowedRoles={allowedRoles}
        application={application}
        cancel={() => setApplication(null)}
        clinet={client}
        refetch={refetch}
      />

      <OwcTypography
        id={generateID.UUID(applicationTable, `applicationtable`, "group_title")}
        className="header-title"
      >
        Top Banner Notification List
      </OwcTypography>
      <div className="container-parent">
        <div className="container-child">
          <span className="filter-text">Filter:</span>
          <OwcSelectDropdown
            label="Select Site"
            dataTestid="equipment-filter-dropdown"
            name="site"
            onSelectChange={(ev) => {
              const selectedOption = siteOptions[ev.detail[0]];
              if (selectedOption) {
                console.log(selectedOption.label);
                if (selectedOption.value === "Show All") {
                  setSiteName("Show All");
                } else {
                  setSiteName(selectedOption.value);
                }
              }
            }}
          >
            {siteOptions.map((option, index) => (
              <OwcListItem
                key={option.value}
                value={option.value}
                data-testid={"equipment-filter-menu-item-" + (option.value)}
              >
                {option.label}
              </OwcListItem>
            ))}
          </OwcSelectDropdown>
          <span className="filter-text-2">Status:</span>
          <CustomSingleSelect
            onChange={(e) => {
              if (e) {
                if (e === "All Status") {
                  setIsActive("All Status");
                } else {
                  setIsActive(e);
                }
              }
            }}
            options={activeOptions}
            selectLabel={"Select Status"}
            showColor={true}
            name="status"
            value={isActive}
            propValue={"value"}
            propLabel={"label"}
          />
        </div>
        <div>
          <OwcButton
            onClick={() => setApplication(emptyApplication)}
          >
            {BUTTON_MODEL_LABEL.addBanner}
          </OwcButton>
        </div>
      </div>
      <div className="container-table">
        <div className="container-table-child">
          <TopBannerMessageList
            isReverseOrder={isReverseOrder}
            orderBy={orderBy[0]}
            pageNo={pageNo}
            setPageNo={setPageNo}
            showPerPage={showPerPage}
            setShowPerPage={setShowPerPage}
            totalCount={totalCount}
            setTotalCount={setTotalCount}
            onRequestSort={(key, isReverse) => {
              setIsReverseOrder(isReverse);
              setOrderBy([key]);
            }}
            data={filteredData}
            meta={metaData({ setApplication, setApplicationToDelete, openDialog })}
            setApplication={handlerOnSetApp}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  categories: state.user.allGroups,
  allowedRoles: selectAllowedRolesFromApps(ownProps.data)
});

export default compose(connect(mapStateToProps, { loadUserInfo: loadUserInfoActions }), withApollo)(TopBannerMessagePageTable);